import React, { useState, useMemo, useEffect, useCallback } from "react";
import { ALL_MONTHS, COMPANY_OPTIONS, DEFAULT_COMPANY_ID, DEFAULT_EXPENSE_ITEMS, DEFAULT_START_MONTH, PAGE_META } from "./constants";
import { getMonths, mkMap, makeId, getMonthStatusLabel, normalizeSimpleActuals } from "./utils";
import { loadLS, saveLS, loadDB, saveDB } from "./storage";
import { supabase, supabaseConfigError } from "./supabaseClient";
import { makeInitial } from "./initialData";
import { NavTab, DropdownNavTab, HeaderInfoPills } from "./ui/SharedUI";
import Dashboard from "./components/Dashboard";
import RevenueEditor from "./components/RevenueEditor";
import SalaryEditor from "./components/SalaryEditor";
import TransportEditor from "./components/TransportEditor";
import ExpenseEditor from "./components/ExpenseEditor";
import ActualsEditor from "./components/ActualsEditor";
import CashflowSimulator from "./components/CashflowSimulator";
import LoanLedger from "./components/LoanLedger";
import Settings from "./components/Settings";

function useWindowWidth() {
  const [width, setWidth] = React.useState(window.innerWidth);
  React.useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return width;
}

function getFirmNameFromQuery() {
  if (typeof window === "undefined") return "";
  const params = new URLSearchParams(window.location.search);
  return (params.get("firm") || "").trim();
}

function formatSavedAt(value) {
  if (!value) return null;
  const d = new Date(value);
  if (!Number.isNaN(d.getTime())) {
    return d.toLocaleString("ja-JP");
  }
  return value;
}

function ensureExpenseItems(items) {
  const base = Array.isArray(items) ? items : DEFAULT_EXPENSE_ITEMS;
  const existingIds = new Set(base.map((item) => item.id));
  const missing = DEFAULT_EXPENSE_ITEMS.filter((item) => !existingIds.has(item.id));
  return missing.length ? [...base, ...missing] : base;
}

export default function App() {
  const ini = useMemo(() => makeInitial(getMonths(DEFAULT_START_MONTH)), []);
  const saved = useMemo(() => loadLS(DEFAULT_COMPANY_ID), []);

  const [authUser, setAuthUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [magicSent, setMagicSent] = useState(false);
  const [magicError, setMagicError] = useState("");
  const [saveState, setSaveState] = useState("idle"); // idle | saving | saved | localOnly
  const [saveError, setSaveError] = useState("");
  const [loadSource, setLoadSource] = useState(saved ? "local" : "none");
  const [companyId, setCompanyId] = useState(DEFAULT_COMPANY_ID);
  const firmName = useMemo(() => getFirmNameFromQuery(), []);
  const [isCompanyDataLoading, setIsCompanyDataLoading] = useState(false);

  const dbLoadedRef = React.useRef(false);
  const savedSnapshotRef = React.useRef(null);

  const [startMonth, setStartMonth] = useState(saved?.startMonth ?? DEFAULT_START_MONTH);
  const defaultCompanyLabel = useMemo(() => COMPANY_OPTIONS.find((c) => c.id === DEFAULT_COMPANY_ID)?.label ?? "", []);
  const [companyName, setCompanyName] = useState(saved?.companyName ?? defaultCompanyLabel);
  const months = useMemo(() => getMonths(startMonth), [startMonth]);

  const [revenue, setRevenue] = useState(saved?.revenue ?? ini.revenue);
  const [staff, setStaff] = useState(saved?.staff ?? ini.staff);
  const [expenses, setExpenses] = useState(saved?.expenses ?? ini.expenses);
  const [expenseItems, setExpenseItems] = useState(ensureExpenseItems(saved?.expenseItems ?? DEFAULT_EXPENSE_ITEMS));
  const [expOverride, setExpOverride] = useState(saved?.expOverride ?? { salary: false, bonus: false, transport: false });
  const [actuals, setActuals] = useState(() => ({
    ...(saved?.actuals ?? ini.actuals),
    simple: normalizeSimpleActuals(saved?.actuals?.simple ?? ini.actuals.simple, getMonths(saved?.startMonth ?? 5))
  }));
  const [cfSettings, setCfSettings] = useState(saved?.cfSettings ?? ini.cfSettings);
  const [loans, setLoans] = useState(saved?.loans ?? []);
  const [tab, setTab] = useState("dashboard");
  const [editorName, setEditorName] = useState(authUser?.email ?? saved?.editorName ?? "");
  const [savedAt, setSavedAt] = useState(saved?.savedAtIso ?? saved?.savedAt ?? null);
  const [savedBy, setSavedBy] = useState(saved?.savedBy ?? null);
  const [dirty, setDirty] = useState(false);
  const [expenseMemos, setExpenseMemos] = useState(saved?.expenseMemos ?? {});
  const [taxNote, setTaxNote] = useState(saved?.taxNote ?? "");
  const [diffComments, setDiffComments] = useState(saved?.diffComments ?? {});
  const windowWidth = useWindowWidth();
  const isMobile = windowWidth <= 768;

  useEffect(() => {
    if (!supabase) {
      setAuthLoading(false);
      return;
    }

    let alive = true;

    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (!alive) return;
      if (error) {
        console.error("[yojitsu] getSession error:", error);
      }
      setAuthUser(session?.user ?? null);
      if (!session?.user) {
        dbLoadedRef.current = false;
        savedSnapshotRef.current = null;
        setDirty(false);
      }
      setAuthLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!alive) return;
      setAuthUser(session?.user ?? null);
      if (!session?.user) {
        dbLoadedRef.current = false;
        savedSnapshotRef.current = null;
        setDirty(false);
      }
      setAuthLoading(false);
      setMagicSent(false);
      setMagicError("");
    });

    return () => {
      alive = false;
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (authUser?.email) {
      setEditorName(authUser.email);
    }
  }, [authUser]);

  useEffect(() => {
    if (!authUser) {
      dbLoadedRef.current = false;
      savedSnapshotRef.current = null;
      setIsCompanyDataLoading(false);
      return;
    }

    const currentSaved = loadLS(companyId);
    let alive = true;
    dbLoadedRef.current = false;
    savedSnapshotRef.current = null;
    setIsCompanyDataLoading(true);

    loadDB(companyId).then((result) => {
      if (!alive) return;

      if (!result.ok) {
        setLoadSource(currentSaved ? "local" : "none");
        setSaveError(currentSaved ? "Supabaseからの読込に失敗しました。ローカル保存データで継続します。" : "Supabaseからの読込に失敗しました。接続設定を確認してください。");
        if (currentSaved) {
          const localStartMonth = currentSaved.startMonth ?? (COMPANY_OPTIONS.find((c) => c.id === companyId)?.defaultStartMonth ?? DEFAULT_START_MONTH);
          const localActuals = {
            ...(currentSaved.actuals ?? ini.actuals),
            simple: normalizeSimpleActuals(currentSaved.actuals?.simple ?? ini.actuals.simple, getMonths(localStartMonth))
          };
          setStartMonth(localStartMonth);
          setCompanyName(currentSaved.companyName ?? (COMPANY_OPTIONS.find((c) => c.id === companyId)?.label ?? ""));
          setRevenue(currentSaved.revenue ?? ini.revenue);
          setStaff(currentSaved.staff ?? ini.staff);
          setExpenses(currentSaved.expenses ?? ini.expenses);
          setExpenseItems(ensureExpenseItems(currentSaved.expenseItems ?? DEFAULT_EXPENSE_ITEMS));
          setExpOverride(currentSaved.expOverride ?? { salary: false, bonus: false, transport: false });
          setActuals(localActuals);
          setCfSettings(currentSaved.cfSettings ?? ini.cfSettings);
          setLoans(currentSaved.loans ?? []);
          setExpenseMemos(currentSaved.expenseMemos ?? {});
          setTaxNote(currentSaved.taxNote ?? "");
          setDiffComments(currentSaved.diffComments ?? {});
          if (currentSaved.savedAtIso !== undefined) setSavedAt(currentSaved.savedAtIso);
          else if (currentSaved.savedAt !== undefined) setSavedAt(currentSaved.savedAt);
          if (currentSaved.savedBy !== undefined) setSavedBy(currentSaved.savedBy);
          savedSnapshotRef.current = JSON.stringify({
            startMonth: localStartMonth,
            companyName: currentSaved.companyName ?? (COMPANY_OPTIONS.find((c) => c.id === companyId)?.label ?? ""),
            revenue: currentSaved.revenue ?? ini.revenue,
            staff: currentSaved.staff ?? ini.staff,
            expenses: currentSaved.expenses ?? ini.expenses,
            expenseItems: ensureExpenseItems(currentSaved.expenseItems ?? DEFAULT_EXPENSE_ITEMS),
            expOverride: currentSaved.expOverride ?? { salary: false, bonus: false, transport: false },
            actuals: localActuals,
            cfSettings: currentSaved.cfSettings ?? ini.cfSettings,
            expenseMemos: currentSaved.expenseMemos ?? {},
            taxNote: currentSaved.taxNote ?? "",
            diffComments: currentSaved.diffComments ?? {},
            loans: currentSaved.loans ?? [],
          });
          dbLoadedRef.current = true;
          setDirty(false);
        }
        return;
      }

      const dbData = result.data;
      if (!dbData) {
        setLoadSource(currentSaved ? "local" : "none");
        if (currentSaved) {
          const localStartMonth = currentSaved.startMonth ?? (COMPANY_OPTIONS.find((c) => c.id === companyId)?.defaultStartMonth ?? DEFAULT_START_MONTH);
          const localActuals = {
            ...(currentSaved.actuals ?? ini.actuals),
            simple: normalizeSimpleActuals(currentSaved.actuals?.simple ?? ini.actuals.simple, getMonths(localStartMonth))
          };
          setStartMonth(localStartMonth);
          setCompanyName(currentSaved.companyName ?? (COMPANY_OPTIONS.find((c) => c.id === companyId)?.label ?? ""));
          setRevenue(currentSaved.revenue ?? ini.revenue);
          setStaff(currentSaved.staff ?? ini.staff);
          setExpenses(currentSaved.expenses ?? ini.expenses);
          setExpenseItems(ensureExpenseItems(currentSaved.expenseItems ?? DEFAULT_EXPENSE_ITEMS));
          setExpOverride(currentSaved.expOverride ?? { salary: false, bonus: false, transport: false });
          setActuals(localActuals);
          setCfSettings(currentSaved.cfSettings ?? ini.cfSettings);
          setLoans(currentSaved.loans ?? []);
          setExpenseMemos(currentSaved.expenseMemos ?? {});
          setTaxNote(currentSaved.taxNote ?? "");
          setDiffComments(currentSaved.diffComments ?? {});
          if (currentSaved.savedAtIso !== undefined) setSavedAt(currentSaved.savedAtIso);
          else if (currentSaved.savedAt !== undefined) setSavedAt(currentSaved.savedAt);
          if (currentSaved.savedBy !== undefined) setSavedBy(currentSaved.savedBy);
          savedSnapshotRef.current = JSON.stringify({
            startMonth: localStartMonth,
            companyName: currentSaved.companyName ?? (COMPANY_OPTIONS.find((c) => c.id === companyId)?.label ?? ""),
            revenue: currentSaved.revenue ?? ini.revenue,
            staff: currentSaved.staff ?? ini.staff,
            expenses: currentSaved.expenses ?? ini.expenses,
            expenseItems: ensureExpenseItems(currentSaved.expenseItems ?? DEFAULT_EXPENSE_ITEMS),
            expOverride: currentSaved.expOverride ?? { salary: false, bonus: false, transport: false },
            actuals: localActuals,
            cfSettings: currentSaved.cfSettings ?? ini.cfSettings,
            expenseMemos: currentSaved.expenseMemos ?? {},
            taxNote: currentSaved.taxNote ?? "",
            diffComments: currentSaved.diffComments ?? {},
            loans: currentSaved.loans ?? [],
          });
        } else {
          savedSnapshotRef.current = JSON.stringify({
            startMonth,
            companyName,
            revenue,
            staff,
            expenses,
            expenseItems,
            expOverride,
            actuals,
            cfSettings,
            expenseMemos,
            taxNote,
            diffComments,
            loans,
          });
        }
        dbLoadedRef.current = true;
        setDirty(false);
        return;
      }

      const nextStartMonth = dbData.startMonth ?? startMonth;
      const nextCompanyName = dbData.companyName ?? companyName;
      const nextRevenue = dbData.revenue ?? revenue;
      const nextStaff = dbData.staff ?? staff;
      const nextExpenses = dbData.expenses ?? expenses;
      const nextExpenseItems = ensureExpenseItems(dbData.expenseItems ?? expenseItems);
      const nextExpOverride = dbData.expOverride ?? expOverride;
      const nextActuals = dbData.actuals !== undefined
        ? {
            ...actuals,
            ...dbData.actuals,
            simple: normalizeSimpleActuals(
              dbData.actuals?.simple ?? actuals.simple,
              getMonths(nextStartMonth)
            )
          }
        : actuals;
      const nextCfSettings = dbData.cfSettings ?? cfSettings;
      const nextLoans = dbData.loans ?? loans;
      const nextExpenseMemos = dbData.expenseMemos ?? expenseMemos;
      const nextTaxNote = dbData.taxNote ?? taxNote;
      const nextDiffComments = dbData.diffComments ?? diffComments;

      if (dbData.startMonth !== undefined) setStartMonth(nextStartMonth);
      if (dbData.companyName !== undefined) setCompanyName(nextCompanyName);
      if (dbData.revenue !== undefined) setRevenue(nextRevenue);
      if (dbData.staff !== undefined) setStaff(nextStaff);
      if (dbData.expenses !== undefined) setExpenses(nextExpenses);
      if (dbData.expenseItems !== undefined) setExpenseItems(nextExpenseItems);
      if (dbData.expOverride !== undefined) setExpOverride(nextExpOverride);
      if (dbData.actuals !== undefined) setActuals(nextActuals);
      if (dbData.cfSettings !== undefined) setCfSettings(nextCfSettings);
      if (dbData.loans !== undefined) setLoans(nextLoans);
      if (dbData.expenseMemos !== undefined) setExpenseMemos(nextExpenseMemos);
      if (dbData.taxNote !== undefined) setTaxNote(nextTaxNote);
      if (dbData.diffComments !== undefined) setDiffComments(nextDiffComments);
      if (dbData.editorName !== undefined) setEditorName(dbData.editorName);
      if (dbData.savedAtIso !== undefined) setSavedAt(dbData.savedAtIso);
      else if (dbData.savedAt !== undefined) setSavedAt(dbData.savedAt);
      if (dbData.savedBy !== undefined) setSavedBy(dbData.savedBy);
      setSaveError("");
      setLoadSource("db");
      setDirty(false);

      savedSnapshotRef.current = JSON.stringify({
        startMonth: nextStartMonth,
        companyName: nextCompanyName,
        revenue: nextRevenue,
        staff: nextStaff,
        expenses: nextExpenses,
        expenseItems: nextExpenseItems,
        expOverride: nextExpOverride,
        actuals: nextActuals,
        cfSettings: nextCfSettings,
        expenseMemos: nextExpenseMemos,
        taxNote: nextTaxNote,
        diffComments: nextDiffComments,
        loans: nextLoans,
      });
      dbLoadedRef.current = true;
    }).finally(() => {
      if (!alive) return;
      setIsCompanyDataLoading(false);
    });

    return () => { alive = false; };
  }, [authUser, companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!dbLoadedRef.current) return;
    if (savedSnapshotRef.current === null) return;
    if (isCompanyDataLoading) {
      setDirty(false);
      return;
    }

    const current = JSON.stringify({
      startMonth,
      companyName,
      revenue,
      staff,
      expenses,
      expenseItems,
      expOverride,
      actuals,
      cfSettings,
      expenseMemos,
      taxNote,
      diffComments,
      loans,
    });
    const isDirty = current !== savedSnapshotRef.current;
    setDirty(isDirty);
    if (!isDirty && (saveState === "saved" || saveState === "localOnly")) {
      setSaveState("idle");
    }
  }, [revenue, staff, expenses, expenseItems, expOverride, actuals, cfSettings, expenseMemos, startMonth, companyName, editorName, taxNote, diffComments, loans, saveState, isCompanyDataLoading]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (!dirty) return;
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [dirty]);

  const buildPayload = useCallback(() => {
    const savedAtIso = new Date().toISOString();
    return {
      startMonth, companyName, revenue, staff, expenses, expenseItems,
      expOverride, actuals, cfSettings, expenseMemos, editorName,
      taxNote, diffComments, loans, savedAtIso, savedAt: savedAtIso, savedBy: editorName
    };
  }, [startMonth, companyName, revenue, staff, expenses, expenseItems, expOverride, actuals, cfSettings, expenseMemos, editorName, taxNote, diffComments, loans]);

  const handleSave = useCallback(async () => {
    const payload = buildPayload();

    setSaveState("saving");
    setSaveError("");

    const lsResult = saveLS(payload, companyId);
    if (!lsResult.ok) {
      setSaveState("idle");
      setSaveError("ブラウザ保存に失敗しました。容量やプライベートモード設定を確認してください。");
      return;
    }

    const dbResult = await saveDB(payload, editorName, companyId);
    if (!dbResult.ok) {
      setSaveState("localOnly");
      setSaveError("ブラウザには保存しましたが、Supabaseへの保存に失敗しました。通信状況と設定を確認してください。");
      return;
    }

    setSavedAt(payload.savedAtIso);
    setSavedBy(editorName);
    setLoadSource("db");
    savedSnapshotRef.current = JSON.stringify({
      startMonth: payload.startMonth,
      companyName: payload.companyName,
      revenue: payload.revenue,
      staff: payload.staff,
      expenses: payload.expenses,
      expenseItems: payload.expenseItems,
      expOverride: payload.expOverride,
      actuals: payload.actuals,
      cfSettings: payload.cfSettings,
      expenseMemos: payload.expenseMemos,
      taxNote: payload.taxNote,
      diffComments: payload.diffComments,
      loans: payload.loans,
    });

    setDirty(false);
    setSaveState("saved");
    setTimeout(() => setSaveState((prev) => (prev === "saved" ? "idle" : prev)), 3000);
  }, [buildPayload, editorName, companyId]);

  const handleRetryDBSave = useCallback(async () => {
    const payload = buildPayload();
    setSaveState("saving");
    setSaveError("");

    const dbResult = await saveDB(payload, editorName, companyId);
    if (!dbResult.ok) {
      setSaveState("localOnly");
      setSaveError("Supabaseへの再保存に失敗しました。通信状況と設定を確認してください。");
      return;
    }

    setSavedAt(payload.savedAtIso);
    setSavedBy(editorName);
    setLoadSource("db");
    savedSnapshotRef.current = JSON.stringify({
      startMonth: payload.startMonth,
      companyName: payload.companyName,
      revenue: payload.revenue,
      staff: payload.staff,
      expenses: payload.expenses,
      expenseItems: payload.expenseItems,
      expOverride: payload.expOverride,
      actuals: payload.actuals,
      cfSettings: payload.cfSettings,
      expenseMemos: payload.expenseMemos,
      taxNote: payload.taxNote,
      diffComments: payload.diffComments,
      loans: payload.loans,
    });

    setDirty(false);
    setSaveState("saved");
    setTimeout(() => setSaveState((prev) => (prev === "saved" ? "idle" : prev)), 3000);
  }, [buildPayload, editorName, companyId]);


  const handleChangeCompany = (newCompanyId) => {
    if (dirty && !window.confirm(`未保存のデータがあります。会社を切り替えると失われます。\nよろしいですか？`)) return;
    const company = COMPANY_OPTIONS.find((c) => c.id === newCompanyId);
    setCompanyId(newCompanyId);
    setCompanyName(company?.label ?? newCompanyId);
    const nextStartMonth = company?.defaultStartMonth ?? DEFAULT_START_MONTH;
    const nm = getMonths(nextStartMonth);
    const freshIni = makeInitial(nm);
    setStartMonth(nextStartMonth);
    setRevenue(freshIni.revenue);
    setStaff(freshIni.staff);
    setExpenses(freshIni.expenses);
    setExpenseItems(DEFAULT_EXPENSE_ITEMS);
    setExpOverride({ salary: false, bonus: false, transport: false });
    setActuals(freshIni.actuals);
    setCfSettings(freshIni.cfSettings);
    setLoans([]);
    setExpenseMemos({});
    setTaxNote("");
    setDiffComments({});
    setSavedAt(null);
    setSavedBy(null);
    setDirty(false);
    setSaveError("");
    setSaveState("idle");
    setLoadSource("none");
  };

  const handleChangeStartMonth = (sm) => {
    const hasActuals = actuals && (
      Object.keys(actuals.revItems || {}).length > 0 ||
      Object.values(actuals.items || {}).some((mv) => Object.values(mv).some((v) => v !== "")) ||
      (actuals.simple?.revItems || []).some((item) => Object.values(item.monthly || {}).some((v) => v !== "")) ||
      (actuals.simple?.expItems || []).some((item) => Object.values(item.monthly || {}).some((v) => v !== ""))
    );
    if (hasActuals && !window.confirm("会計期間を変更すると入力済みの実績データがリセットされます。\nよろしいですか？")) return;
    const nm = getMonths(sm);
    setStartMonth(sm);
    setRevenue((prev) => prev.map((r) => ({ ...r, monthly: Object.fromEntries(nm.map((m) => [m, r.monthly[m] ?? 0])) })));
    setStaff((prev) => prev.map((s) => ({
      ...s,
      salary: Object.fromEntries(nm.map((m) => [m, s.salary[m] ?? 0])),
      bonus: Object.fromEntries(nm.map((m) => [m, s.bonus[m] ?? 0])),
      transport: Object.fromEntries(nm.map((m) => [m, s.transport[m] ?? 0])),
    })));
    setExpenses((prev) => Object.fromEntries(Object.entries(prev).map(([id, mv]) => [id, Object.fromEntries(nm.map((m) => [m, mv[m] ?? 0]))])));
    setActuals({
      revItems: {},
      items: Object.fromEntries(expenseItems.map((e) => [e.id, mkMap(nm, "")])),
      simple: normalizeSimpleActuals({
        revItems: [{ id: makeId("simpleRev"), name: "売上合計", monthly: mkMap(nm, "") }],
        expItems: [{ id: makeId("simpleExp"), name: "費用合計", monthly: mkMap(nm, "") }]
      }, nm)
    });
    setExpenseMemos({});
  };

  const handleMagicLink = async (e) => {
    e.preventDefault();
    setMagicError("");

    if (!supabase) {
      setMagicError(supabaseConfigError || "Supabase設定が不足しています。");
      return;
    }

    const { error } = await supabase.auth.signInWithOtp({
      email: email.trim(),
      options: { emailRedirectTo: window.location.origin },
    });

    if (error) {
      setMagicError(error.message);
      return;
    }

    setMagicSent(true);
  };

  const handleLogout = async () => {
    if (!supabase) return;
    const { error } = await supabase.auth.signOut();
    if (error) {
      setSaveError("ログアウトに失敗しました。もう一度お試しください。");
    }
  };

  const headerMonthStatus = useMemo(() => months.map((m) => {
    const hasRevDetail = revenue.some((r) => (actuals.revItems?.[r.id]?.[m] ?? "") !== "");
    const hasRevSimple = (actuals.simple?.revItems || []).some((item) => (item.monthly?.[m] ?? "") !== "") || (actuals.simple?.rev?.[m] ?? "") !== "";
    const hasExpDetail = expenseItems.some((item) => (actuals.items?.[item.id]?.[m] ?? "") !== "");
    const hasExpSimple = (actuals.simple?.expItems || []).some((item) => (item.monthly?.[m] ?? "") !== "") || (actuals.simple?.exp?.[m] ?? "") !== "";
    return { month: m, status: getMonthStatusLabel(hasRevDetail || hasRevSimple, hasExpDetail || hasExpSimple) };
  }), [months, revenue, actuals, expenseItems]);

  const filledCount = headerMonthStatus.filter((x) => x.status.label === "入力済み").length + headerMonthStatus.filter((x) => x.status.label === "一部入力").length;
  const partialCount = headerMonthStatus.filter((x) => x.status.label === "一部入力").length;
  const completeCount = headerMonthStatus.filter((x) => x.status.label === "入力済み").length;
  const fiscalLabel = `${ALL_MONTHS[startMonth - 1]}〜${ALL_MONTHS[(startMonth + 10) % 12]}期`;
  const m = PAGE_META[tab] || PAGE_META.dashboard;

  if (authLoading) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#F4F7F4", fontFamily: "'Noto Sans JP',sans-serif" }}>
        <span style={{ color: "#888", fontSize: "14px" }}>読み込み中…</span>
      </div>
    );
  }

  if (supabaseConfigError) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#F4F7F4", fontFamily: "'Noto Sans JP',sans-serif", padding: "24px" }}>
        <div style={{ background: "#fff", border: "1px solid #E8EDE8", borderRadius: "12px", padding: "32px", maxWidth: "520px", width: "100%" }}>
          <div style={{ fontSize: "20px", fontWeight: 800, color: "#1A3A1A", marginBottom: "12px" }}>Supabase設定エラー</div>
          <div style={{ fontSize: "14px", color: "#555", lineHeight: 1.7 }}>{supabaseConfigError}</div>
          <div style={{ fontSize: "12px", color: "#888", marginTop: "14px", lineHeight: 1.7 }}>
            REACT_APP_SUPABASE_URL と REACT_APP_SUPABASE_ANON_KEY を設定したうえで再読み込みしてください。
          </div>
        </div>
      </div>
    );
  }

  if (!authUser) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#F4F7F4", fontFamily: "'Noto Sans JP',sans-serif" }}>
        <div style={{ background: "#fff", border: "1px solid #E8EDE8", borderRadius: "12px", padding: "40px 48px", maxWidth: "400px", width: "100%", textAlign: "center" }}>
          <div style={{ fontSize: "20px", fontWeight: "800", color: "#1A3A1A", marginBottom: "8px", letterSpacing: "-0.02em" }}>予実管理</div>
          <div style={{ fontSize: "13px", color: "#888", marginBottom: "8px" }}>{firmName || "株式会社富士見"}</div>
          {firmName && <div style={{ fontSize: "11px", color: "#AA7722", marginBottom: "24px", fontWeight: "700" }}>デモ表示：{firmName}</div>}
          {magicSent ? (
            <div>
              <div style={{ fontSize: "14px", color: "#2D7A2D", fontWeight: "700", marginBottom: "8px" }}>メールを送信しました</div>
              <div style={{ fontSize: "12px", color: "#666" }}>受信したメールのリンクをクリックしてログインしてください。</div>
            </div>
          ) : (
            <form onSubmit={handleMagicLink}>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="メールアドレス"
                required
                style={{ width: "100%", padding: "10px 12px", border: "1px solid #D0D8D0", borderRadius: "6px", fontSize: "14px", fontFamily: "'Noto Sans JP',sans-serif", marginBottom: "12px", boxSizing: "border-box" }}
              />
              {magicError && <div style={{ color: "#C0392B", fontSize: "12px", marginBottom: "8px" }}>{magicError}</div>}
              <button
                type="submit"
                style={{ width: "100%", padding: "10px", background: "#2D7A2D", color: "#fff", border: "none", borderRadius: "6px", fontSize: "14px", fontWeight: "700", cursor: "pointer", fontFamily: "'Noto Sans JP',sans-serif" }}
              >
                ログインリンクを送る
              </button>
            </form>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "#F4F7F4", fontFamily: "'Noto Sans JP','Hiragino Sans',sans-serif", position: "relative" }}>
      {isCompanyDataLoading && authUser && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(244, 247, 244, 0.72)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2000, backdropFilter: "blur(1px)" }}>
          <div style={{ background: "#fff", border: "1px solid #E8EDE8", borderRadius: "12px", padding: "14px 18px", boxShadow: "0 8px 24px rgba(0,0,0,0.08)", fontSize: "13px", color: "#1A3A1A", fontWeight: "700" }}>
            会社データを読み込み中…
          </div>
        </div>
      )}
      <div style={{ background: "#fff", borderBottom: "1px solid #E8EDE8", padding: isMobile ? "0 12px" : "0 24px", position: "relative", zIndex: 100, overflow: "visible" }}>
        {firmName && (
          <div style={{ maxWidth: "1600px", margin: "0 auto", paddingTop: "12px" }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: "8px", background: "#FFF7E8", color: "#8A5A00", border: "1px solid #F2D7A6", borderRadius: "999px", padding: "6px 12px", fontSize: "11px", fontWeight: "700", fontFamily: "'Noto Sans JP',sans-serif" }}>
              <span>提携事務所デモ</span>
              <span>{firmName}</span>
            </div>
          </div>
        )}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingTop: "16px", paddingBottom: "8px", maxWidth: "1600px", margin: "0 auto" }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: "12px" }}>
            <span style={{ fontSize: "18px", fontWeight: "800", color: "#1A3A1A", letterSpacing: "-0.02em" }}>予実管理</span>
            <span style={{ fontSize: "12px", color: "#888", background: "#F0F4F0", padding: "2px 8px", borderRadius: "999px" }}>{fiscalLabel}</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap", justifyContent: "flex-end" }}>
            <label style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "11px", color: "#666", fontFamily: "'Noto Sans JP',sans-serif" }}>
              <span>会社</span>
              <select
                value={companyId}
                onChange={(e) => handleChangeCompany(e.target.value)}
                style={{ padding: "5px 8px", border: "1px solid #D0D8D0", borderRadius: "6px", fontSize: "12px", background: "#fff", color: "#1A3A1A", fontFamily: "'Noto Sans JP',sans-serif", fontWeight: "700" }}
              >
                {COMPANY_OPTIONS.map((c) => (
                  <option key={c.id} value={c.id}>{c.label}</option>
                ))}
              </select>
            </label>
            <span style={{ fontSize: "11px", color: "#888", fontFamily: "'Noto Sans JP',sans-serif" }}>
              {authUser?.email}
            </span>
            {savedAt && !dirty && !isMobile && <span style={{ fontSize: "11px", color: "#aaa", fontFamily: "'Noto Sans JP',sans-serif" }}>💾 {formatSavedAt(savedAt)} 保存済</span>}
            {dirty && <span style={{ fontSize: "11px", color: "#AA7722", fontFamily: "'Noto Sans JP',sans-serif", fontWeight: "700" }}>● {isMobile ? "" : "未保存"}</span>}
            {saveState === "saving" && <span style={{ fontSize: "11px", color: "#2D7A2D", fontWeight: "700" }}>保存中…</span>}
            {saveState === "saved" && !dirty && <span style={{ fontSize: "11px", color: "#2D7A2D", fontWeight: "700" }}>DB保存完了</span>}
            {saveState === "localOnly" && <span style={{ fontSize: "11px", color: "#C56A00", fontWeight: "700" }}>LSのみ保存</span>}
            {loadSource === "db" && <span style={{ fontSize: "11px", color: "#2D7A2D", fontWeight: "700" }}>DB読込</span>}
            {loadSource === "local" && <span style={{ fontSize: "11px", color: "#8A6D1F", fontWeight: "700" }}>LS継続中</span>}
            <button onClick={handleSave} disabled={saveState === "saving"} style={{ padding: "6px 14px", background: dirty ? "#2D7A2D" : "#E8F4E8", color: dirty ? "#fff" : "#2D7A2D", border: "1px solid #2D7A2D", borderRadius: "6px", cursor: saveState === "saving" ? "default" : "pointer", fontSize: "12px", fontFamily: "'Noto Sans JP',sans-serif", fontWeight: "700", transition: "all 0.15s", opacity: saveState === "saving" ? 0.7 : 1 }}>
              {saveState === "saving" ? "保存中…" : "保存"}
            </button>
            <button onClick={handleLogout} style={{ padding: "6px 12px", background: "transparent", color: "#aaa", border: "1px solid #D0D8D0", borderRadius: "6px", cursor: "pointer", fontSize: "11px", fontFamily: "'Noto Sans JP',sans-serif" }}>
              ログアウト
            </button>
            <div style={{ fontSize: "16px", color: "#1A3A1A", fontWeight: "800", fontFamily: "'Noto Sans JP',sans-serif", letterSpacing: "-0.02em" }}>{companyName}</div>
          </div>
        </div>
        {saveError && (
          <div style={{ maxWidth: "1600px", margin: "0 auto", paddingBottom: "8px" }}>
            <div style={{ background: "#FFF4E8", color: "#9A4D00", border: "1px solid #F2D1AA", borderRadius: "8px", padding: "8px 12px", fontSize: "12px", lineHeight: 1.6, display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", flexWrap: "wrap" }}>
              <span>{saveError}</span>
              {saveState === "localOnly" && (
                <button onClick={handleRetryDBSave} style={{ padding: "6px 10px", background: "#fff", color: "#9A4D00", border: "1px solid #F2D1AA", borderRadius: "6px", cursor: "pointer", fontSize: "12px", fontWeight: "700" }}>
                  Supabaseへ再保存
                </button>
              )}
            </div>
          </div>
        )}
        <div className="tab-nav" style={{ display: "flex", gap: "2px", maxWidth: "1600px", margin: "0 auto", overflowX: isMobile ? "auto" : "visible", position: "relative", zIndex: 100, WebkitOverflowScrolling: "touch", msOverflowStyle: "none", scrollbarWidth: "none" }}>
          <NavTab label="ダッシュボード" tabId="dashboard" active={tab === "dashboard"} onClick={() => setTab("dashboard")} badge={filledCount > 0 ? `${filledCount}月入力済` : null} />
          <NavTab label="収入設定" tabId="revenue" active={tab === "revenue"} onClick={() => setTab("revenue")} />
          <DropdownNavTab label="経費設定" tabId="expenses" active={tab === "expenses"} subActive={["salary", "transport"].includes(tab) ? tab : null} onClick={() => setTab("expenses")} onSubClick={(id) => setTab(id)} subItems={[{ id: "expenses", label: "経費設定" }, { id: "salary", label: "給与・賞与" }, { id: "transport", label: "交通費" }]} />
          <NavTab label="実績入力" tabId="actuals" active={tab === "actuals"} onClick={() => setTab("actuals")} />
          <NavTab label="CFシミュ" tabId="cashflow" active={tab === "cashflow"} onClick={() => setTab("cashflow")} />
          <NavTab label="借入台帳" tabId="loans" active={tab === "loans"} onClick={() => setTab("loans")} />
          <NavTab label="⚙ 設定" tabId="settings" active={tab === "settings"} onClick={() => setTab("settings")} />
        </div>
        <HeaderInfoPills editorName={editorName} savedAt={formatSavedAt(savedAt)} savedBy={savedBy} completeCount={completeCount} partialCount={partialCount} />
      </div>

      <div style={{ background: m.bg, borderBottom: `3px solid ${m.accent}`, padding: "0 24px", position: "relative", zIndex: 1 }}>
        <div style={{ maxWidth: "1600px", margin: "0 auto", padding: "18px 0 14px" }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: "14px" }}>
            <span style={{ fontSize: "22px", fontWeight: "900", color: m.color, letterSpacing: "-0.03em", lineHeight: 1 }}>{m.label}</span>
            <span style={{ fontSize: "12px", color: m.accent, fontWeight: "600", opacity: 0.8 }}>{m.sub}</span>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: "1600px", margin: "0 auto", padding: isMobile ? "12px" : "20px 24px" }}>
        {tab === "dashboard" && <Dashboard revenue={revenue} expenses={expenses} expenseItems={expenseItems} expOverride={expOverride} staff={staff} actuals={actuals} months={months} taxNote={taxNote} diffComments={diffComments} onChangeDiffComment={(month, text) => setDiffComments((prev) => ({ ...prev, [month]: text }))} />}
        {tab === "revenue" && <RevenueEditor revenue={revenue} setRevenue={setRevenue} months={months} />}
        {tab === "salary" && <SalaryEditor staff={staff} setStaff={setStaff} months={months} />}
        {tab === "transport" && <TransportEditor staff={staff} setStaff={setStaff} months={months} />}
        {tab === "expenses" && <ExpenseEditor expenses={expenses} setExpenses={setExpenses} expenseItems={expenseItems} setExpenseItems={setExpenseItems} expOverride={expOverride} setExpOverride={setExpOverride} staff={staff} months={months} expenseMemos={expenseMemos} setExpenseMemos={setExpenseMemos} loans={loans} />}
        {tab === "actuals" && <ActualsEditor actuals={actuals} setActuals={setActuals} revenue={revenue} expenses={expenses} expenseItems={expenseItems} expOverride={expOverride} staff={staff} months={months} taxNote={taxNote} onChangeTaxNote={setTaxNote} />}
        {tab === "cashflow" && <CashflowSimulator revenue={revenue} expenses={expenses} expenseItems={expenseItems} expOverride={expOverride} staff={staff} months={months} cfSettings={cfSettings} setCfSettings={setCfSettings} loans={loans} />}
        {tab === "loans" && <LoanLedger loans={loans} setLoans={setLoans} months={months} />}
        {tab === "settings" && <Settings startMonth={startMonth} onChangeStartMonth={handleChangeStartMonth} companyName={companyName} onChangeCompanyName={setCompanyName} />}
      </div>
    </div>
  );
}
